package fr.arcplex.network.template

import fr.arcplex.network.logging.Logger
import fr.arcplex.network.http.Serializer
import kotlinx.serialization.json.*

// kdljs 0.3.0 node shape: { name, values: [], properties: {}, children: [] }.
private fun nodeName(node: JsonObject): String =
    (node["name"] as? JsonPrimitive)?.contentOrNull ?: ""

private fun nodeValues(node: JsonObject): JsonArray =
    node["values"] as? JsonArray ?: JsonArray(emptyList())

private fun nodeChildren(node: JsonObject): JsonArray =
    node["children"] as? JsonArray ?: JsonArray(emptyList())

private fun nodeValue(node: JsonObject): String? =
    nodeValues(node).firstOrNull()?.let {
        if (it is JsonNull) null else (it as? JsonPrimitive)?.content
    }

private fun getAttr(node: JsonObject, name: String): String? {
    val props = node["properties"] as? JsonObject ?: return null
    val v = props[name] ?: return null
    if (v is JsonNull) return null
    return (v as? JsonPrimitive)?.content
}

private fun getAttrInt(node: JsonObject, name: String): Int? {
    return getAttr(node, name)?.toIntOrNull()
}

private fun nodeAttrs(node: JsonObject): Map<String, String> {
    val props = node["properties"] as? JsonObject ?: return emptyMap()
    return props.entries.mapNotNull { (k, v) ->
        if (v is JsonNull) null else (v as? JsonPrimitive)?.content?.let { k to it }
    }.toMap()
}

actual fun parseCoherenceTemplate(kdlBody: String): CoherenceProgramTemplate {
    val doc = try {
        Serializer.json.parseToJsonElement(kdlParseToJson(kdlBody)).jsonArray
    } catch (e: Exception) {
        Logger.e("CoherenceApi", "kdl doc decode failed: ${e.message}")
        return CoherenceProgramTemplate()
    }
    val nodes = doc.mapNotNull { it as? JsonObject }

    // The API wraps all sections in a single top-level `template` node; older/flat bodies
    // keep them as top-level siblings. Support both.
    var templateNode: JsonObject? = null
    for (node in nodes) {
        if (nodeName(node) == "template") {
            templateNode = node
            break
        }
    }
    // In the flat format `template` is a leaf and the sections are its top-level siblings.
    val templateChildren = templateNode?.let { nodeChildren(it) }
    val useNestedSections = templateChildren != null && templateChildren.size > 0
    val sections = if (useNestedSections) {
        templateChildren!!.mapNotNull { it as? JsonObject }
    } else {
        nodes
    }

    var contentType = ""
    var box = ""
    var revision = 0
    var platform = ""
    var rendererMinVersion = ""
    var minVersion = ""
    var aspect = ""
    var gridColumns = 0
    var gridGutter = 0
    var gridWidth = 0
    var gridHeight = 0
    var safeArea = ""
    val regions = mutableListOf<CoherenceRegion>()

    if (templateNode != null) {
        contentType = nodeValue(templateNode) ?: ""
        box = getAttr(templateNode, "box") ?: ""
        revision = getAttrInt(templateNode, "revision") ?: 0
    }

    for (node in sections) {
        when (nodeName(node)) {
            "requires" -> {
                for (child in nodeChildren(node).mapNotNull { it as? JsonObject }) {
                    when (nodeName(child)) {
                        "platform" -> platform = nodeValue(child) ?: ""
                        "renderer" -> rendererMinVersion = getAttr(child, "min") ?: ""
                        "registry" -> minVersion = getAttr(child, "min-version") ?: ""
                        "aspect" -> aspect = nodeValue(child) ?: ""
                    }
                }
            }
            "design" -> {
                gridWidth = getAttrInt(node, "width") ?: 0
                gridHeight = getAttrInt(node, "height") ?: 0
                safeArea = getAttr(node, "safe-area") ?: ""
            }
            "grid" -> {
                gridColumns = getAttrInt(node, "columns") ?: 0
                gridGutter = getAttrInt(node, "gutter") ?: 0
            }
            "region" -> {
                val region = parseRegionJson(node)
                if (region != null) regions.add(region)
            }
        }
    }

    return CoherenceProgramTemplate(
        contentType = contentType,
        ratio = aspect,
        revision = revision,
        registryVersion = minVersion.toIntOrNull() ?: 0,
        box = box,
        platform = platform,
        rendererMinVersion = rendererMinVersion,
        minVersion = minVersion,
        gridWidth = gridWidth,
        gridHeight = gridHeight,
        gridColumns = gridColumns,
        gridGutter = gridGutter,
        safeArea = safeArea,
        regions = regions,
        attrs = templateNode?.let { nodeAttrs(it) } ?: emptyMap()
    )
}

private fun parseRegionJson(node: JsonObject): CoherenceRegion? {
    val name = nodeValue(node) ?: return null
    val col = getAttrInt(node, "col") ?: 0
    val span = getAttrInt(node, "span") ?: 0
    val height = getAttr(node, "height") ?: ""

    val layers = mutableListOf<CoherenceLayer>()
    val rails = mutableListOf<CoherenceRailDef>()
    val slots = mutableListOf<CoherenceSlot>()

    for (child in nodeChildren(node).mapNotNull { it as? JsonObject }) {
        when (nodeName(child)) {
            "layer" -> {
                val layer = parseLayerJson(child)
                if (layer != null) layers.add(layer)
            }
            "rail" -> {
                val rail = parseRailJson(child)
                if (rail != null) rails.add(rail)
            }
            "slot" -> {
                val slot = parseSlotJson(child)
                if (slot != null) slots.add(slot)
            }
        }
    }

    return CoherenceRegion(
        name = name,
        col = col,
        span = span,
        height = height,
        layers = layers,
        rails = rails,
        slots = slots,
        attrs = nodeAttrs(node)
    )
}

private fun parseLayerJson(node: JsonObject): CoherenceLayer {
    val padding = getAttr(node, "padding") ?: ""
    val slots = mutableListOf<CoherenceSlot>()
    val rows = mutableListOf<CoherenceRow>()
    val columns = mutableListOf<CoherenceColumn>()

    for (child in nodeChildren(node).mapNotNull { it as? JsonObject }) {
        when (nodeName(child)) {
            "slot" -> {
                val slot = parseSlotJson(child)
                if (slot != null) slots.add(slot)
            }
            "row" -> {
                val row = parseRowJson(child)
                if (row != null) rows.add(row)
            }
            "col" -> {
                val col = parseColumnJson(child)
                if (col != null) columns.add(col)
            }
        }
    }

    return CoherenceLayer(
        padding = padding,
        slots = slots,
        rows = rows,
        columns = columns,
        attrs = nodeAttrs(node)
    )
}

private fun parseSlotJson(node: JsonObject): CoherenceSlot {
    val name = nodeValue(node) ?: ""
    val anchor = getAttr(node, "anchor") ?: ""
    val maxItems = getAttrInt(node, "max_items") ?: 0
    val maxLines = getAttrInt(node, "max_lines") ?: 0

    return CoherenceSlot(
        name = name,
        anchor = anchor,
        maxItems = maxItems,
        maxLines = maxLines,
        attrs = nodeAttrs(node)
    )
}

private fun parseRowJson(node: JsonObject): CoherenceRow {
    val gap = getAttr(node, "gap") ?: ""
    val align = getAttr(node, "align") ?: ""
    val slots = mutableListOf<CoherenceSlotRef>()
    val actions = mutableListOf<CoherenceActionDef>()

    for (child in nodeChildren(node).mapNotNull { it as? JsonObject }) {
        when (nodeName(child)) {
            "slot" -> {
                val slot = parseSlotJson(child)
                if (slot != null) {
                    slots.add(CoherenceSlotRef(name = slot.name, maxItems = slot.maxItems))
                }
            }
            "action" -> {
                val action = parseActionJson(child)
                if (action != null) actions.add(action)
            }
        }
    }

    return CoherenceRow(
        gap = gap,
        align = align,
        slots = slots,
        actions = actions,
        attrs = nodeAttrs(node)
    )
}

private fun parseColumnJson(node: JsonObject): CoherenceColumn {
    val gap = getAttr(node, "gap") ?: ""
    val align = getAttr(node, "align") ?: ""
    val slots = mutableListOf<CoherenceSlotRef>()
    val rows = mutableListOf<CoherenceRow>()

    for (child in nodeChildren(node).mapNotNull { it as? JsonObject }) {
        when (nodeName(child)) {
            "slot" -> {
                val slot = parseSlotJson(child)
                if (slot != null) {
                    slots.add(CoherenceSlotRef(name = slot.name, maxItems = slot.maxItems))
                }
            }
            "row" -> {
                val row = parseRowJson(child)
                if (row != null) rows.add(row)
            }
        }
    }

    return CoherenceColumn(
        gap = gap,
        align = align,
        slots = slots,
        rows = rows,
        attrs = nodeAttrs(node)
    )
}

private fun parseActionJson(node: JsonObject): CoherenceActionDef {
    val name = nodeValue(node) ?: ""
    val emphasis = getAttr(node, "emphasis") ?: ""
    var bgColor = ""
    for (child in nodeChildren(node).mapNotNull { it as? JsonObject }) {
        if (nodeName(child) == "format") {
            bgColor = getAttr(child, "bg-color") ?: ""
            break
        }
    }

    return CoherenceActionDef(
        name = name,
        emphasis = emphasis,
        bgColor = bgColor,
        attrs = nodeAttrs(node)
    )
}

private fun parseRailJson(node: JsonObject): CoherenceRailDef? {
    val name = nodeValue(node) ?: return null
    val title = getAttr(node, "title") ?: ""
    val max = getAttrInt(node, "max") ?: 0

    return CoherenceRailDef(
        name = name,
        title = title,
        max = max,
        attrs = nodeAttrs(node)
    )
}
